/**
 * Sidebar — 232px (220px in compact density) navigation column.
 *
 * Reads the navigation manifest from `@k-os/core/navigation`. The host
 * passes the active route id and a `renderItem` callback so the actual
 * link element (`<Link>` from TanStack Router, `<a>`, `<button>`, etc.)
 * is the host's choice — keeps this package framework-agnostic.
 */

import { Icon, type IconName } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import type { NavGroup, NavItem } from '@k-os/core';
import styles from './Sidebar.module.css';

export interface SidebarUser {
  name: string;
  initials: string;
  meta?: string | null;
  color?: string | null;
}

export interface SidebarProps {
  groups: readonly NavGroup[];
  /** id of the currently active item — see NAV_GROUPS in @k-os/core. */
  activeId?: string | null;
  brandName?: React.ReactNode;
  brandLogoSrc?: string;
  brandLogoAlt?: string;
  renderBrandMark?: (props: SidebarBrandRenderProps) => React.ReactNode;
  renderBrandName?: (props: SidebarBrandRenderProps) => React.ReactNode;
  /** Slot at the right end of the brand row (e.g. an app switcher). */
  brandTrailing?: React.ReactNode;
  /** Per-item count badge, keyed by id. */
  counts?: Record<string, number | undefined>;
  /** Per-item alert label, keyed by id (e.g. "2"). */
  alerts?: Record<string, string | undefined>;
  /** Item ids that should render with a muted, non-interactive appearance. */
  disabledIds?: readonly string[];
  /**
   * Render a single item's outer element. The host returns a `<Link>` /
   * `<a>` / `<button>` of its choice. Children are pre-built (icon + name
   * + optional count); the host only owns the outer `data-active` element.
   */
  renderItem: (item: NavItem, props: SidebarItemRenderProps) => React.ReactNode;
  user?: SidebarUser | null;
  versionLabel?: string;
  /** Slot below the brand for a primary CTA (Quick Add) and Search. */
  topSlot?: React.ReactNode;
  /** Slot at the right end of the footer (e.g. settings icon). */
  footerSlot?: React.ReactNode;
  /**
   * Override the avatar + name + meta block in the footer. Lets the host
   * wrap the user card in a popover trigger / link without lifting the
   * Avatar import into this package. The default uses `user`.
   */
  renderUserCard?: (user: SidebarUser) => React.ReactNode;
}

export interface SidebarItemRenderProps {
  className: string;
  children: React.ReactNode;
}

export interface SidebarBrandRenderProps {
  className: string;
  children: React.ReactNode;
}

export function Sidebar({
  groups,
  activeId,
  brandName = 'Tasks',
  brandLogoSrc,
  brandLogoAlt = '',
  renderBrandMark,
  renderBrandName,
  brandTrailing,
  counts,
  alerts,
  disabledIds,
  renderItem,
  user,
  versionLabel,
  topSlot,
  footerSlot,
  renderUserCard,
}: SidebarProps) {
  const disabledSet = disabledIds ? new Set(disabledIds) : null;
  const brandMarkClassName = [
    styles.brandMark,
    brandLogoSrc ? styles.brandMarkImage : '',
  ]
    .filter(Boolean)
    .join(' ');
  const brandMarkChildren = brandLogoSrc ? (
    <img src={brandLogoSrc} alt={brandLogoAlt} />
  ) : null;
  const brandMark = renderBrandMark ? (
    renderBrandMark({ className: brandMarkClassName, children: brandMarkChildren })
  ) : (
    <span className={brandMarkClassName} aria-hidden>
      {brandMarkChildren}
    </span>
  );
  const brandNameNode = renderBrandName ? (
    renderBrandName({ className: styles.brandName, children: brandName })
  ) : (
    <span className={styles.brandName}>{brandName}</span>
  );

  return (
    <aside className={styles.sidebar} aria-label="Primary navigation">
      <div className={styles.brand}>
        {brandMark}
        {brandNameNode}
        {versionLabel && <span className={styles.brandSub}>{versionLabel}</span>}
        {brandTrailing && <span className={styles.brandTrailing}>{brandTrailing}</span>}
      </div>

      {topSlot}

      {groups.map((group) => (
        <div className={styles.section} key={group.group}>
          <div className={styles.sectionLabel}>{group.group}</div>
          {group.items.map((item) => {
            const active = item.id === activeId;
            const disabled = disabledSet?.has(item.id) ?? false;
            const className = [
              styles.item,
              active ? styles.itemActive : '',
              disabled ? styles.itemDisabled : '',
            ]
              .filter(Boolean)
              .join(' ');
            const count = counts?.[item.id];
            const alert = alerts?.[item.id];
            return (
              <div key={item.id}>
                {renderItem(item, {
                  className,
                  children: (
                    <>
                      <span className={styles.icon}>
                        <Icon name={item.icon as IconName} size={14} />
                      </span>
                      <span>{item.name}</span>
                      {(count != null || alert) && (
                        <span
                          style={{
                            marginLeft: 'auto',
                            fontFamily: 'var(--font-mono)',
                            fontSize: '11px',
                            color: alert ? 'var(--pri-critical)' : 'var(--ink-4)',
                          }}
                        >
                          {/* When only `alert` is set (no accompanying total
                              count), show it alone rather than the
                              Tasks-style "{overdue}·{total}" pairing — e.g.
                              Health's "Active workout" item just has a
                              count of active sessions, not a subset of one. */}
                          {alert ? (count != null ? `${alert}·${count}` : alert) : count}
                        </span>
                      )}
                    </>
                  ),
                })}
              </div>
            );
          })}
        </div>
      ))}

      {user && (
        <div className={styles.footer}>
          {renderUserCard ? (
            renderUserCard(user)
          ) : (
            <>
              <Avatar
                person={{ initials: user.initials, color: user.color ?? null }}
                size={22}
              />
              <div className={styles.footerInfo}>
                <div className={styles.footerName}>{user.name}</div>
                {user.meta && <div className={styles.footerMeta}>{user.meta}</div>}
              </div>
            </>
          )}
          {footerSlot}
        </div>
      )}
    </aside>
  );
}
